import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
} from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import { generateRandomString } from '@fc-shared/utils/data.utils';
import { MatRipple } from '@angular/material/core';

@Component({
  selector: 'fc-checkbox',
  template: `
    <div
      class="target relative flex h-40 w-40 shrink-0 items-center justify-center rounded-full"
      matRipple
      [matRippleColor]="rippleColor()"
      [matRippleDisabled]="disabled()"
      (click)="$event.stopPropagation()"
    >
      <input
        (change)="onChange($event)"
        [id]="id"
        type="checkbox"
        [checked]="checked()"
        [disabled]="disabled()"
        [indeterminate]="indeterminate()"
        class="absolute z-2 h-full w-full cursor-pointer opacity-0 disabled:cursor-default"
      />
      <div class="box flex h-20 w-20 items-center justify-center rounded-sm">
        @if (indeterminate()) {
          <fc-icon icon="icon-remove" [color]="iconColor()" [size]="16" />
        } @else if (checked()) {
          <fc-icon icon="icon-tick" [color]="iconColor()" [size]="16" />
        }
      </div>
    </div>
    <label
      [for]="id"
      class="flex cursor-pointer flex-col gap-2 pl-4 text-sm leading-20"
      [class.cursor-default]="disabled()"
    >
      <span
        class="font-medium"
        [class.text-grey-900]="!disabled()"
        [class.text-secondary-200]="disabled()"
      >
        <ng-content></ng-content>
      </span>
      @if (description(); as description) {
        <span
          [class.text-grey-600]="!disabled()"
          [class.text-secondary-200]="disabled()"
        >
          {{ description }}
        </span>
      }
    </label>
  `,
  styles: `
    @layer checkbox {
      .target::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background-color: var(--color-secondary-500);
        opacity: 0;
        pointer-events: none;
      }

      :host(.selected) .target::before {
        background-color: var(--color-primary-600);
      }

      :host(:not(.disabled):hover) .target::before {
        opacity: 0.08;
      }

      :host(:not(.disabled)) .target:has(input:focus-visible)::before,
      :host(:not(.disabled)) .target:active::before {
        opacity: 0.12;
      }

      .box {
        border: 1.5px solid var(--color-secondary-300);
      }

      :host(.selected) .box {
        border-color: transparent;
        background-color: var(--color-primary-600);
      }

      :host(.disabled) .box {
        border-color: var(--color-secondary-100);
        background-color: var(--color-secondary-50);
      }

      :host(.disabled.selected) .box {
        border-color: transparent;
        background-color: var(--color-secondary-200);
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, MatRipple],
  host: {
    class: 'flex items-center',
    '[class.items-start]': '!!description()',
    '[class.disabled]': 'disabled()',
    '[class.selected]': 'isSelected()',
  },
})
export class Checkbox {
  id = generateRandomString(4);
  checked = model<boolean>(false);
  indeterminate = input<boolean>(false);
  disabled = input<boolean>(false);
  description = input<string>();

  protected readonly isSelected = computed(
    () => this.checked() || this.indeterminate(),
  );

  protected readonly iconColor = computed(() =>
    this.disabled() ? 'secondary50' : 'white',
  );

  protected readonly rippleColor = computed(() =>
    this.isSelected()
      ? 'color-mix(in srgb, var(--color-primary-600) 10%, transparent)'
      : 'color-mix(in srgb, var(--color-secondary-500) 10%, transparent)',
  );

  onChange(event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.checked.set(isChecked);
  }
}
